import { constant, every, times } from 'lodash-es';
import styled, { DefaultTheme, ThemedStyledProps } from 'styled-components';
import { getThemeColor, getThemeFontFamily } from 'utils/styled';
import { InputBaseStyleProps } from './types';

function getInputPadding(
  props: ThemedStyledProps<InputBaseStyleProps, DefaultTheme>,
) {
  const defaultPadding = '17px';
  const adornmentEdgePadding = '0px';

  const { $hasEndAdornment, $hasStartAdornment } = props;

  const padding = times(4, constant(defaultPadding));

  if ($hasStartAdornment) {
    padding.splice(3, 1, adornmentEdgePadding);
  }

  if ($hasEndAdornment) {
    padding.splice(1, 1, adornmentEdgePadding);
  }

  if (padding.length === 4 && every(padding, defaultPadding)) {
    return defaultPadding;
  }

  return padding.join(' ');
}

function getRootPadding(
  props: ThemedStyledProps<InputBaseStyleProps, DefaultTheme>,
) {
  const defaultPadding = '0px';
  const adornmentEdgePadding = '14px';

  const { $hasEndAdornment, $hasStartAdornment } = props;

  const padding = times(4, constant(defaultPadding));

  if ($hasStartAdornment) {
    padding.splice(3, 1, adornmentEdgePadding);
  }

  if ($hasEndAdornment) {
    padding.splice(1, 1, adornmentEdgePadding);
  }

  if (padding.length === 4 && every(padding, defaultPadding)) {
    return defaultPadding;
  }

  return padding.join(' ');
}

export const InputBaseRoot = styled.div<InputBaseStyleProps>`
  align-items: center;
  background-color: ${getThemeColor('l2')};
  border-radius: 6px;
  display: inline-flex;
  height: 50px;
  overflow: hidden;
  padding: ${getRootPadding};
  position: relative;

  &[data-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &::after {
    border: 1px solid #ccc;
    border-radius: inherit;
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: ${(props) =>
      props.theme.transitions.create('border-color', '300ms')};

    ${({ $borderless }) => {
      return $borderless ? 'border: none;' : '';
    }}
    z-index: 1;
  }

  &[data-focused='true']::after {
    border-color: ${getThemeColor('actionPrimary')};
    border-width: 2px;
  }

  &[data-invalid='true']::after {
    border-color: ${getThemeColor('danger')};
    border-width: 2px;
  }
`;

export const Input = styled.input`
  border: none;
  color: ${getThemeColor('d1')};
  cursor: inherit;
  font-family: ${getThemeFontFamily('primary')};
  font-size: 14px;
  line-height: 1.07;
  pointer-events: auto;
  padding: ${getInputPadding};
  width: 100%;

  &,
  &[disabled] {
    background-color: inherit;
  }

  &::placeholder {
    color: ${getThemeColor('d5')};
  }

  &:focus {
    outline: none;
  }
`;
